import React, { useState, useEffect } from "react";
import { StyleSheet, ScrollView, View, Alert } from "react-native";
import { router, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { useTheme } from "@/hooks/useTheme";
import { MaterialIcons } from "@expo/vector-icons";

export default function VerifyScreen() {
  const { colors } = useTheme();
  const { session } = useLocalSearchParams();
  const [phoneNumber, setPhoneNumber] = useState("(555) 123-4567"); // Pre-filled from info step
  const [verificationCode, setVerificationCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    setLoading(true);
    try {
      // TODO: Implement SMS verification
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setCodeSent(true);
      setCountdown(60);
      Alert.alert("Code Sent", `Verification code sent to ${phoneNumber}`);
    } catch (error) {
      Alert.alert("Error", "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) {
      Alert.alert("Invalid Code", "Please enter a 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement code verification
      await new Promise((resolve) => setTimeout(resolve, 1000));
      Alert.alert("Success!", "Phone number verified successfully!");
    } catch (error) {
      Alert.alert("Error", "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    Alert.alert("Registration Complete", "Welcome to RockForce! Your account has been created successfully.");
    // TODO: Navigate to main app or success screen
  };

  return (
    <ScrollView style={[styles.container, { backgroundColor: colors.background }]}>
      <View style={styles.content}>
        <ThemedText style={[styles.title, { color: colors.text }]}>Verify Phone Number</ThemedText>
        <ThemedText style={[styles.subtitle, { color: colors.textSecondary }]}>
          We&apos;ll send a verification code to confirm your phone number
        </ThemedText>

        <View style={[styles.phoneSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.phoneHeader}>
            <MaterialIcons name="phone" size={24} color={colors.primary} />
            <ThemedText style={[styles.phoneLabel, { color: colors.text }]}>Phone Number</ThemedText>
          </View>
          <ThemedText style={[styles.phoneNumber, { color: colors.text }]}>{phoneNumber}</ThemedText>
          <ThemedText style={[styles.phoneHint, { color: colors.textSecondary }]}>
            From your profile information
          </ThemedText>
        </View>

        {!codeSent ? (
          <Button
            text="Send Verification Code"
            iconPosition="right"
            onPress={handleSendCode}
            icon="message"
            iconSize={18}
            iconColor="white"
            style={[styles.sendButton, { backgroundColor: colors.primary }]}
            textStyle={{ color: "white", fontSize: 16 }}
            enabled={!loading}
          />
        ) : (
          <View style={styles.verificationSection}>
            <Input
              icon="security"
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChangeText={setVerificationCode}
              keyboardType="numeric"
              maxLength={6}
              style={styles.codeInput}
              autoFocus={true}
            />

            <View style={styles.codeActions}>
              <Button
                text="Verify Code"
                iconPosition="right"
                onPress={handleVerifyCode}
                icon="check-decagram"
                iconSize={18}
                iconColor="white"
                style={[
                  styles.verifyButton,
                  {
                    backgroundColor: verificationCode.length === 6 ? colors.primary : colors.border,
                    opacity: verificationCode.length === 6 ? 1 : 0.5,
                  },
                ]}
                textStyle={{ color: "white", fontSize: 16 }}
                enabled={verificationCode.length === 6 && !loading}
              />

              {countdown > 0 ? (
                <ThemedText style={[styles.countdownText, { color: colors.textSecondary }]}>
                  Resend code in {countdown}s
                </ThemedText>
              ) : (
                <Button
                  text="Resend Code"
                  onPress={handleSendCode}
                  style={[styles.resendButton, { borderColor: colors.border, borderWidth: 1 }]}
                  textStyle={{ color: colors.text, fontSize: 14 }}
                  enabled={!loading}
                />
              )}
            </View>
          </View>
        )}

        <View style={[styles.completionSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <MaterialIcons name="check-circle-outline" size={32} color={colors.emerald?.text || colors.primary} />
          <ThemedText style={[styles.completionTitle, { color: colors.text }]}>Almost Done!</ThemedText>
          <ThemedText style={[styles.completionText, { color: colors.textSecondary }]}>
            After verifying your phone number, your account will be ready to use.
          </ThemedText>

          {codeSent && verificationCode.length === 6 && (
            <Button
              text="Complete Registration"
              onPress={() => router.replace("/")}
              icon="check"
              iconSize={18}
              iconColor="white"
              style={[styles.completeButton, { backgroundColor: colors.emerald?.background || colors.primary }]}
              textStyle={{ color: "white", fontSize: 16, fontWeight: "600" }}
            />
          )}
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 32,
  },
  phoneSection: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginBottom: 24,
  },
  phoneHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  phoneLabel: {
    fontSize: 16,
    fontWeight: "600",
  },
  phoneNumber: {
    fontSize: 18,
    fontWeight: "500",
    marginBottom: 4,
  },
  phoneHint: {
    fontSize: 14,
  },
  sendButton: {
    borderRadius: 8,
    minHeight: 48,
    marginBottom: 32,
  },
  verificationSection: {
    gap: 16,
    marginBottom: 32,
  },
  codeInput: {
    textAlign: "center",
  },
  codeActions: {
    gap: 12,
  },
  verifyButton: {
    borderRadius: 8,
    minHeight: 48,
  },
  resendButton: {
    backgroundColor: "transparent",
    borderRadius: 8,
    minHeight: 40,
  },
  countdownText: {
    textAlign: "center",
    fontSize: 14,
  },
  completionSection: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 20,
    alignItems: "center",
    gap: 12,
  },
  completionTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  completionText: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 8,
  },
  completeButton: {
    borderRadius: 8,
    minHeight: 48,
    minWidth: 200,
  },
});
